import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingApi } from '../services/api';
import { OnboardingStatus, OnboardingStepKey } from '@dsgvo-buddy/types';
import { ONBOARDING_STEP_TEXTS, ONBOARDING_STEP_ORDER } from '../constants/onboarding';
import {
  Title,
  Text,
  Button,
  FlexBox,
  Card,
  Icon,
  BusyIndicator,
  MessageStrip,
  ProgressIndicator,
  ObjectStatus
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedStep, setExpandedStep] = useState<OnboardingStepKey | null>(null);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await onboardingApi.getStatus();
      if (res.success && res.data) {
        setStatus(res.data);
        const firstOpen = res.data.steps.find(s => !s.completed && !s.dismissed);
        if (firstOpen) setExpandedStep(firstOpen.stepKey);
      } else {
        setError(res.error || 'Onboarding-Status konnte nicht geladen werden');
      }
    } catch {
      setError('Verbindung zum Server fehlgeschlagen');
    }
    setLoading(false);
  };

  const handleConfirm = async (stepKey: OnboardingStepKey) => {
    const res = await onboardingApi.confirmStep(stepKey);
    if (res.success && res.data) setStatus(res.data);
  };

  const handleDismiss = async (stepKey: OnboardingStepKey) => {
    const res = await onboardingApi.dismissStep(stepKey);
    if (res.success && res.data) setStatus(res.data);
  };

  const handleReset = async (stepKey: OnboardingStepKey) => {
    const res = await onboardingApi.resetStep(stepKey);
    if (res.success && res.data) setStatus(res.data);
  };

  if (loading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  if (error) {
    return (
      <div className="page-layout--form">
        <Title level="H3" style={{ marginBottom: '1rem' }}>Datenschutz-Kompass</Title>
        <MessageStrip design="Negative" hideCloseButton>
          {error}
        </MessageStrip>
        <Button design="Default" onClick={loadStatus} icon="refresh" style={{ marginTop: '1rem' }}>
          Erneut versuchen
        </Button>
      </div>
    );
  }

  if (!status) return null;

  const stepsMap = new Map(status.steps.map(s => [s.stepKey, s]));
  const isManualStep = (key: OnboardingStepKey) => key === 'dsr_process' || key === 'breach_process';

  return (
    <div className="page-layout--form">
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Title level="H3" style={{ marginBottom: '0.5rem' }}>Datenschutz-Kompass</Title>
        <Text style={{ color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '1rem' }}>
          Ihr Leitfaden zum vollständigen DSGVO-Konzept. Arbeiten Sie die Schritte der Reihe nach ab
          — oder springen Sie direkt zum Thema, das Sie gerade brauchen.
        </Text>

        {/* Overall progress */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.25rem' }}>
          <Text style={{ fontWeight: '600', fontSize: '0.875rem' }}>
            Gesamtfortschritt: {status.overallProgress}%
          </Text>
          <Text style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem' }}>
            ({status.steps.filter(s => s.completed).length} von {status.steps.length} Schritten erledigt)
          </Text>
        </div>
        <ProgressIndicator
          value={status.overallProgress}
          displayValue={`${status.overallProgress}%`}
          
          style={{ width: '100%' }}
        />
      </div>

      {status.overallProgress === 100 && (
        <MessageStrip design="Positive" hideCloseButton style={{ marginBottom: '1.5rem' }}>
          Alle Datenschutz-Grundlagen sind dokumentiert. Ihr DSGVO-Konzept steht auf einem soliden Fundament.
          Denken Sie daran, Ihre Dokumentation regelmäßig zu aktualisieren.
        </MessageStrip>
      )}

      {/* Step cards */}
      <FlexBox direction="Column" style={{ gap: '0.75rem' }}>
        {ONBOARDING_STEP_ORDER.map(stepKey => {
          const step = stepsMap.get(stepKey);
          if (!step) return null;
          const text = ONBOARDING_STEP_TEXTS[stepKey];
          const isExpanded = expandedStep === stepKey;
          const isComplete = step.completed;
          const isDismissed = step.dismissed;

          return (
            <Card key={stepKey} style={{ opacity: isDismissed ? 0.6 : 1 }}>
              {/* Card header — clickable to expand/collapse */}
              <div
                style={{
                  padding: '1rem 1.25rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                }}
                onClick={() => setExpandedStep(isExpanded ? null : stepKey)}
              >
                {/* Status icon */}
                <div style={{
                  width: '2.25rem',
                  height: '2.25rem',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background: isComplete
                    ? 'var(--sapPositiveBackground, #e8f5e9)'
                    : isDismissed
                      ? 'var(--sapNeutralBackground, #f5f5f5)'
                      : step.progress.current > 0
                        ? 'var(--sapInformativeBackground, #e3f2fd)'
                        : 'var(--sapNeutralBackground, #f5f5f5)',
                }}>
                  <Icon
                    name={isComplete ? 'accept' : isDismissed ? 'less' : step.progress.current > 0 ? 'pending' : text.icon}
                    style={{
                      color: isComplete
                        ? 'var(--sapPositiveColor)'
                        : isDismissed
                          ? 'var(--sapNeutralColor)'
                          : step.progress.current > 0
                            ? 'var(--sapInformativeColor)'
                            : 'var(--sapNeutralColor)',
                      fontSize: '1rem',
                    }}
                  />
                </div>

                {/* Title + progress */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Text style={{ fontWeight: '600', fontSize: '0.9375rem', color: isComplete ? 'var(--sapPositiveColor)' : '#1a1d21' }}>
                      {text.number}. {text.title}
                    </Text>
                    {isComplete && (
                      <ObjectStatus state="Success" showDefaultIcon style={{ fontSize: '0.75rem' }}>Erledigt</ObjectStatus>
                    )}
                    {isDismissed && !isComplete && (
                      <ObjectStatus state="None" style={{ fontSize: '0.75rem' }}>Ausgeblendet</ObjectStatus>
                    )}
                  </div>
                  {!isComplete && !isDismissed && step.progress.target > 1 && (
                    <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                      {step.progress.current} von {step.progress.target}
                    </Text>
                  )}
                </div>

                {/* Expand chevron */}
                <Icon
                  name={isExpanded ? 'slim-arrow-up' : 'slim-arrow-down'}
                  style={{ color: 'var(--sapNeutralColor)', fontSize: '0.875rem' }}
                />
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)' }}>
                  <div style={{ paddingTop: '1rem' }}>
                    <Text style={{ lineHeight: '1.6', display: 'block', marginBottom: '1rem', color: '#556170' }}>
                      {text.description}
                    </Text>

                    {text.tip && (
                      <MessageStrip design="Information" hideCloseButton style={{ marginBottom: '1rem' }}>
                        Tipp: {text.tip}
                      </MessageStrip>
                    )}

                    {/* Progress bar for multi-target steps */}
                    {step.progress.target > 1 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <ProgressIndicator
                          value={Math.round((step.progress.current / step.progress.target) * 100)}
                          displayValue={`${step.progress.current} / ${step.progress.target}`}
                          
                          style={{ width: '100%' }}
                        />
                      </div>
                    )}

                    {/* Actions */}
                    <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
                      <Button
                        icon={text.icon}
                        design={isComplete ? 'Default' : 'Emphasized'}
                        onClick={() => navigate(text.ctaRoute)}
                      >
                        {text.ctaLabel}
                      </Button>

                      {isManualStep(stepKey) && !isComplete && (
                        <Button design="Default" icon="accept" onClick={() => handleConfirm(stepKey)}>
                          Habe ich verstanden
                        </Button>
                      )}

                      {isManualStep(stepKey) && isComplete && step.manuallyConfirmed && (
                        <Button design="Transparent" icon="undo" onClick={() => handleReset(stepKey)}>
                          Zurücksetzen
                        </Button>
                      )}

                      {!isComplete && !isDismissed && (
                        <Button design="Transparent" icon="less" onClick={() => handleDismiss(stepKey)}>
                          Ausblenden
                        </Button>
                      )}

                      {isDismissed && (
                        <Button design="Transparent" icon="undo" onClick={() => handleReset(stepKey)}>
                          Wieder einblenden
                        </Button>
                      )}
                    </FlexBox>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </FlexBox>
    </div>
  );
}
